/* Карточка воронки разработки: описание, чек-лист, вложения.
   Правится прямо здесь — форма /edit/devTasks/:id остаётся для полей-справочников. */

import { canEdit, crmLevel, ddmm, DEV_KIND_META, devStagesOf, stageColorsOf } from "@mera/shared";
import { Button, List, ListButton } from "konsta/react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "../lib/router.jsx";

import { Ion } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import SwipeRow from "../components/SwipeRow.jsx";
import { SkelDevTask } from "../components/skeleton.jsx";
import { ErrorState, Pill, Sep } from "../components/ui.jsx";
import { api, avatarUrl, uploadFile } from "../lib/api.js";
import { useApp, useFetch } from "../lib/store.jsx";
import { alertDialog, confirmDialog, haptic } from "../lib/tg.js";

export default function DevTaskCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me, meta } = useApp();
  const { data: t, loading, error, refetch, setData } = useFetch(`/dev-tasks/${id}`);

  if (loading) return <Screen back title="Карточка"><SkelDevTask /></Screen>;
  if (error) return <Screen back><ErrorState text={error} onRetry={refetch} /></Screen>;

  const business = (meta?.businesses || []).find((b) => b.id === t.businessId);
  const stages = devStagesOf(business);
  const kindColor = DEV_KIND_META[t.kind]?.color || "var(--tg-hint)";
  // «Только чтение» по воронке разработки: карточка смотрится, но не правится
  const canWrite = canEdit(crmLevel(me, t.businessId, "dev"));

  /* Правку показываем сразу, не дожидаясь ответа: этап и галочки чек-листа должны
     отзываться мгновенно. Ответ сервера перезаписывает карточку, ошибка — откат. */
  const patch = async (body) => {
    const before = t;
    setData({ ...t, ...body });
    try {
      setData(await api.patch(`/dev-tasks/${id}`, body));
    } catch (e) {
      setData(before);
      haptic.error();
      alertDialog(e.message);
    }
  };

  /* Вложения тоже правим на месте — перезапрашивать всю карточку незачем */
  const addAtt = (a) => setData((p) => ({ ...p, atts: [...(p?.atts || []), a] }));
  const dropAtt = (attId) => setData((p) => ({ ...p, atts: (p?.atts || []).filter((a) => a.id !== attId) }));

  const remove = async () => {
    const ok = await confirmDialog(`Удалить карточку «${t.title}»? Вместе с ней уйдут чек-лист и вложения.`);
    if (!ok) return;
    await api.del(`/dev-tasks/${id}`);
    haptic.success();
    navigate(-1);
  };

  return (
    <Screen back title="Карточка">
      {/* Шапка */}
      <div className="px-4 pt-1">
        <div className="card-tg px-4 py-3">
          <div className="display text-[19px] font-semibold leading-tight">{t.title}</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Pill color={kindColor} bg={`color-mix(in srgb, ${kindColor} 14%, transparent)`}>{t.kind}</Pill>
            {t.projName ? <Pill color="var(--tg-hint)" bg="var(--tg-secondary-bg)">{t.projName}</Pill> : null}
            {t.dueDate ? (
              <span className="num inline-flex items-center gap-1 text-[12px]" style={{ color: "var(--tg-hint)" }}>
                <Ion.calendar size={12} /> {ddmm(t.dueDate)}
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1 text-[12px]" style={{ color: "var(--tg-hint)" }}>
              <Ion.person size={12} /> {t.who || "не назначен"}
            </span>
          </div>
        </div>
      </div>

      {/* Этап воронки — меняется прямо на карточке */}
      <div className="hscroll flex gap-2 px-4 pt-3">
        {stages.map((s) => {
          const active = s === t.stage;
          // Цвет этапа из настроек бизнеса; не задан — обычная кнопка Telegram
          const tint = stageColorsOf(business, "dev")[s];
          return (
            <button
              key={s}
              onClick={() => { if (!canWrite) return; haptic.select(); if (!active) patch({ stage: s }); }}
              className="shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold whitespace-nowrap active:opacity-70"
              style={{
                background: active ? tint || "var(--tg-button)" : "var(--tg-section-bg)",
                color: active ? "#fff" : tint || "var(--tg-text)",
              }}
            >
              {s}
            </button>
          );
        })}
      </div>

      <Details value={t.details} onSave={(details) => patch({ details })} canEdit={canWrite} />
      <Checklist items={t.checklist || []} onSave={(checklist) => patch({ checklist })} canEdit={canWrite} />
      <Attachments taskId={id} items={t.atts || []} onAdded={addAtt} onRemoved={dropAtt} canEdit={canWrite} />

      {canWrite ? (
        <>
          <div className="px-4 pt-3">
            <Button large rounded onClick={() => { haptic.light(); navigate(`/edit/devTasks/${id}`); }}>
              Изменить поля
            </Button>
          </div>

          <List strong inset>
            <ListButton colors={{ textIos: "text-[color:var(--fin-red)]" }} onClick={remove}>
              Удалить
            </ListButton>
          </List>
        </>
      ) : null}
    </Screen>
  );
}

/* ── Описание: обычное поле, растёт по мере набора, сохраняется само ── */
function Details({ value, onSave, canEdit = true }) {
  const [text, setText] = useState(value || "");
  const box = useRef(null);
  const timer = useRef(null);

  /* Высота textarea считается по содержимому: сбрасываем и берём scrollHeight,
     иначе поле не умеет уменьшаться после удаления строк */
  const grow = () => {
    const el = box.current;
    if (!el) return;
    el.style.height = "auto";
    /* Пустое поле оставляем однострочным по rows=1: scrollHeight у пустого
       textarea в WebKit бывает выше строки, и плейсхолдер висел не по центру */
    el.style.height = el.value ? `${el.scrollHeight}px` : "";
  };

  useEffect(grow, []);
  useEffect(() => () => clearTimeout(timer.current), []);

  const commit = (next) => {
    clearTimeout(timer.current);
    if ((value || "") === next) return;
    onSave(next || null);
  };

  return (
    <div className="px-4 pt-3">
      <div className="card-tg px-4 py-3">
        <textarea
          ref={box}
          rows={1}
          value={text}
          readOnly={!canEdit}
          placeholder={canEdit ? "Описание, детали, ссылки" : "Описания нет"}
          onChange={(e) => {
            setText(e.target.value);
            grow();
            clearTimeout(timer.current);
            timer.current = setTimeout(() => commit(e.target.value.trim()), 700);
          }}
          onBlur={() => commit(text.trim())}
          className="block w-full resize-none overflow-hidden bg-transparent outline-none text-[15px] leading-snug"
          style={{ color: "var(--tg-text)" }}
        />
      </div>
    </div>
  );
}

/* ── Чек-лист: пункты отмечаются галочкой, удаляются свайпом ── */
function Checklist({ items, onSave, canEdit = true }) {
  const [adding, setAdding] = useState("");

  const nextId = () => items.reduce((max, i) => Math.max(max, Number(i.id) || 0), 0) + 1;

  const toggle = (item) => {
    haptic.select();
    onSave(items.map((i) => (i.id === item.id ? { ...i, done: !i.done } : i)));
  };

  const remove = (item) => {
    haptic.light();
    onSave(items.filter((i) => i.id !== item.id));
  };

  const add = () => {
    const text = adding.trim();
    if (!text) return;
    haptic.success();
    setAdding("");
    onSave([...items, { id: nextId(), text, done: false }]);
  };

  return (
    <>
      {items.length ? (
        <div className="px-4 pt-3">
          <div className="card-tg py-1 overflow-hidden">
            {items.map((it, i) => (
              <React.Fragment key={it.id}>
                {i > 0 ? <Sep /> : null}
                <SwipeRow
                  onDelete={canEdit ? () => remove(it) : null}
                  contentClassName="flex items-start gap-2.5 px-4 py-2.5"
                >
                  <button
                    data-nodrag
                    className="mt-0.5 shrink-0 active:opacity-60"
                    onClick={canEdit ? () => toggle(it) : undefined}
                    disabled={!canEdit}
                    aria-label="Отметить"
                  >
                    {it.done ? (
                      <Ion.checkmarkCircle size={19} style={{ color: "var(--fin-green)" }} />
                    ) : (
                      <Ion.ellipse size={19} style={{ color: "var(--tg-hint)" }} />
                    )}
                  </button>
                  <span
                    className="text-[14px] leading-snug flex-1"
                    style={{
                      textDecoration: it.done ? "line-through" : "none",
                      opacity: it.done ? 0.55 : 1,
                    }}
                  >
                    {it.text}
                  </span>
                </SwipeRow>
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : null}

      {canEdit ? (
      <div className="px-4 pt-2">
        <div className="card-tg p-2.5 flex items-center gap-2">
          <Ion.add size={18} style={{ color: "var(--tg-hint)" }} className="shrink-0" />
          <input
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="Новый пункт"
            className="flex-1 min-w-0 bg-transparent outline-none text-[15px]"
            style={{ color: "var(--tg-text)" }}
          />
          <button
            onClick={add}
            disabled={!adding.trim()}
            className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center active:opacity-70 disabled:opacity-30"
            style={{ background: "var(--tg-button)", color: "var(--tg-button-text)" }}
            aria-label="Добавить пункт"
          >
            <Ion.checkmark size={15} />
          </button>
        </div>
      </div>
      ) : null}
    </>
  );
}

/* ── Вложения: фото и файлы ── */
function Attachments({ taskId, items, onAdded, onRemoved, canEdit = true }) {
  const input = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const pick = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    setErr(null);
    try {
      // Каждый файл появляется в списке сразу, как загрузился, а не когда доедут все
      for (const file of files) {
        const up = await uploadFile(file);
        onAdded(await api.post(`/dev-tasks/${taskId}/attachments`, up));
      }
      haptic.success();
    } catch (e2) {
      haptic.error();
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    const ok = await confirmDialog(`Удалить «${a.name}»?`);
    if (!ok) return;
    onRemoved(a.id);
    haptic.success();
    await api.del(`/dev-tasks/${taskId}/attachments/${a.id}`);
  };

  return (
    <>
      {items.length ? (
        <div className="px-4 pt-3">
          <div className="card-tg py-1 overflow-hidden">
            {items.map((a, i) => (
              <React.Fragment key={a.id}>
                {i > 0 ? <Sep /> : null}
                <SwipeRow onDelete={canEdit ? () => remove(a) : null} contentClassName="flex items-center gap-3 px-4 py-2.5">
                  {a.kind === "image" && a.url ? (
                    <img
                      src={avatarUrl(a.url)}
                      alt=""
                      className="w-10 h-10 rounded-lg object-cover shrink-0"
                      style={{ background: "var(--tg-secondary-bg)" }}
                    />
                  ) : (
                    <span
                      className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: "var(--tg-secondary-bg)" }}
                    >
                      <Ion.documentText size={18} style={{ color: "var(--tg-hint)" }} />
                    </span>
                  )}
                  <a
                    data-nodrag
                    href={avatarUrl(a.url)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[14px] truncate flex-1"
                    style={{ color: "var(--tg-text)" }}
                  >
                    {a.name}
                  </a>
                  <Ion.chevronForward size={15} style={{ color: "var(--tg-hint)" }} className="shrink-0" />
                </SwipeRow>
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : null}

      {canEdit ? (
        <div className="px-4 pt-2">
          <input ref={input} type="file" multiple className="hidden" onChange={pick} />
          <button
            onClick={() => { haptic.light(); input.current?.click(); }}
            disabled={busy}
            className="w-full rounded-xl py-3 text-[15px] font-semibold active:opacity-70 disabled:opacity-50"
            style={{ background: "var(--tg-section-bg)", color: "var(--tg-link)" }}
          >
            {busy ? "Загружаю…" : "Добавить фото или файл"}
          </button>
          {err ? (
            <div className="pt-1.5 text-center text-[13px]" style={{ color: "var(--fin-red)" }}>{err}</div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
